import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useChildMatches } from "@tanstack/react-router";
import { Card, EmptyState, PageHeader, PRIMARY_BTN } from "../components/account-ui.tsx";
import { MapPinIcon, PencilIcon, PlusIcon, SmartphoneIcon, TrashcanIcon } from "../icons/index.tsx";
import { api, type CustomerAddress } from "../lib/api.ts";

export const Route = createFileRoute("/account/address-book")({ component: AddressBook });

export const useAddresses = () =>
  useQuery({
    queryKey: ["addresses"],
    queryFn: () => api.get<{ addresses: CustomerAddress[] }>("/account/addresses"),
  });

function AddressBook() {
  const queryClient = useQueryClient();
  // /account/address-book/$id nests under this route, so the list has to
  // stand aside for it; without the Outlet the form never reaches the page.
  const childMatches = useChildMatches();
  const { data } = useAddresses();
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/account/addresses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
      queryClient.invalidateQueries({ queryKey: ["config"] });
    },
  });
  const locate = useMutation({
    mutationFn: (address: CustomerAddress) =>
      api.patch(`/account/addresses/${address.id}`, {
        useForLocation: address.use_for_location !== 1,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["addresses"] });
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
      await queryClient.invalidateQueries({ queryKey: ["config"] });
    },
  });

  if (childMatches.length > 0) return <Outlet />;
  if (!data) return <div className="h-[100vh]" />;

  return (
    <div>
      <PageHeader
        title="Organisation Locations"
        subtitle="Save as many organisation locations as you need. One of them can be your billing address."
        action={
          <Link to="/account/address-book/$id" params={{ id: "add" }} className={PRIMARY_BTN}>
            <PlusIcon className="h-5 w-5" />
            New Location
          </Link>
        }
      />

      {data.addresses.length === 0 ? (
        <Card bodyClass="">
          <EmptyState
            Icon={MapPinIcon}
            title="No organisation locations"
            message="Add one so checkout can fill itself in next time."
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
          {data.addresses.map((address) => (
            <Card key={address.id} className="flex flex-col" bodyClass="flex flex-1 flex-col p-5">
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900">
                  {address.full_name || "Address"}
                </h2>
                {address.company && (
                  <p className="mt-0.5 text-sm text-gray-500">{address.company}</p>
                )}

                {(address.default_shipping === 1 || address.default_billing === 1) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {address.default_shipping === 1 && <Chip>Default location</Chip>}
                    {address.default_billing === 1 && <Chip>Default billing</Chip>}
                  </div>
                )}

                <div className="mt-4 flex gap-2.5 text-sm text-gray-600">
                  <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                  <p>
                    {address.street_line1}
                    {address.street_line2 && (
                      <>
                        <br />
                        {address.street_line2}
                      </>
                    )}
                    <br />
                    {address.city}
                    {address.province ? `, ${address.province}` : ""} {address.postal_code}
                  </p>
                </div>

                {address.phone && (
                  <div className="mt-2 flex gap-2.5 text-sm text-gray-600">
                    <SmartphoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <p>{address.phone}</p>
                  </div>
                )}
              </div>

              <LocationSwitch
                checked={address.use_for_location === 1}
                disabled={locate.isPending}
                onChange={() => locate.mutate(address)}
              />

              <div className="mt-5 flex items-center gap-2 border-t border-gray-100 pt-4">
                <Link
                  to="/account/address-book/$id"
                  params={{ id: address.id }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                >
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </Link>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(address.id)}
                >
                  <TrashcanIcon width={14} height={14} className="fill-current" />
                  Delete
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function LocationSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={`tooltip tooltip-top mt-4 flex w-fit items-center gap-3 before:max-w-xs before:whitespace-normal ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
      data-tip="The map opens on this address. Only one address can be on, and turning it on turns off Use My Location."
    >
      <input
        type="checkbox"
        role="switch"
        aria-checked={checked}
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span
        aria-hidden
        className="pointer-events-none relative h-5 w-9 shrink-0 rounded-full bg-gray-300 transition-colors peer-checked:bg-primary-600 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-600 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-4"
      />
      <span className="text-sm font-medium text-gray-900">Use Address Location</span>
    </label>
  );
}

function Chip({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-700">
      {children}
    </span>
  );
}
